/**
 * TEMPORARY: carried over from the Framer partner page until the product editor
 * and entitlements (separate branch) own pricing. Delete this folder with that
 * rollout instead of extending it — prices here are not read from anywhere else.
 *
 * Formulas are 1:1 from "Kostenkalkulator BLIKS IO.xlsx".
 */

export const CALCULATOR_MODULES = [
  'omniscient',
  'processAssessment',
  'converter',
  'digitalArchitect',
  'capacityHeatmap',
  'grc',
  'aiProjectPlanner'
] as const;

export type CalculatorModule = (typeof CALCULATOR_MODULES)[number];

interface ModulePrice {
  baseMonthly: number;
  seatMonthly: number;
  yearlyDiscount: number;
}

export const CALCULATOR_PRICES = {
  core: {
    orgManagerMonthly: 990,
    sysAdminMonthly: 150,
    userStartMonthly: 5.5,
    userFloorMonthly: 2.5,
    decay: 5000,
    yearlyDiscount: 0.05
  },
  aiEnablementPerUserMonthly: 2,
  modules: {
    omniscient: { baseMonthly: 500, seatMonthly: 400, yearlyDiscount: 0.05 },
    processAssessment: { baseMonthly: 700, seatMonthly: 500, yearlyDiscount: 0.05 },
    converter: { baseMonthly: 1200, seatMonthly: 500, yearlyDiscount: 0.05 },
    digitalArchitect: { baseMonthly: 400, seatMonthly: 200, yearlyDiscount: 0.05 },
    capacityHeatmap: { baseMonthly: 250, seatMonthly: 200, yearlyDiscount: 0.05 },
    grc: { baseMonthly: 700, seatMonthly: 500, yearlyDiscount: 0.05 },
    aiProjectPlanner: { baseMonthly: 250, seatMonthly: 0, yearlyDiscount: 0.05 }
  } satisfies Record<CalculatorModule, ModulePrice>,
  isoCheck: { baseYearly: 1500, baseQueries: 20, packageYearly: 125, packageQueries: 5 }
} as const;

/** Modules that are a plain on/off switch, without extra access rights. */
export const MODULES_WITHOUT_SEATS: readonly CalculatorModule[] = ['aiProjectPlanner'];

export interface CalculatorInput {
  yearly: boolean;
  users: number;
  sysAdmins: number;
  orgManagers: number;
  aiEnablement: boolean;
  modules: Record<CalculatorModule, { active: boolean; extraSeats: number }>;
  isoCheck: { active: boolean; extraPackages: number };
}

export interface CalculatorResult {
  userPriceMonthly: number;
  coreYearly: number;
  moduleYearly: Record<CalculatorModule, number>;
  modulesYearly: number;
  aiYearly: number;
  isoYearly: number;
  isoQueries: number;
  totalYearly: number;
  /** ISO Check is billed per contract year, so the monthly figure leaves it out. */
  monthlyExcludingIso: number;
  yearlySaving: number;
}

export function userPriceMonthly(users: number): number {
  const { userStartMonthly, userFloorMonthly, decay } = CALCULATOR_PRICES.core;
  return userFloorMonthly + (userStartMonthly - userFloorMonthly) * Math.exp(-users / decay);
}

export function calculateLicenseCost(input: CalculatorInput): CalculatorResult {
  const { core, modules, isoCheck, aiEnablementPerUserMonthly } = CALCULATOR_PRICES;
  const discounted = (gross: number, rate: number) => (input.yearly ? gross * (1 - rate) : gross);

  const perUser = userPriceMonthly(input.users);
  const coreGross =
    (perUser * input.users +
      input.sysAdmins * core.sysAdminMonthly +
      input.orgManagers * core.orgManagerMonthly) *
    12;
  const coreYearly = discounted(coreGross, core.yearlyDiscount);

  let modulesGross = 0;
  const moduleYearly = {} as Record<CalculatorModule, number>;
  for (const module of CALCULATOR_MODULES) {
    const { active, extraSeats } = input.modules[module];
    const price = modules[module];
    const seats = MODULES_WITHOUT_SEATS.includes(module) ? 0 : extraSeats;
    const gross = active ? (price.baseMonthly + seats * price.seatMonthly) * 12 : 0;
    modulesGross += gross;
    moduleYearly[module] = discounted(gross, price.yearlyDiscount);
  }
  const modulesYearly = Object.values(moduleYearly).reduce((sum, value) => sum + value, 0);

  const isoYearly = input.isoCheck.active
    ? isoCheck.baseYearly + input.isoCheck.extraPackages * isoCheck.packageYearly
    : 0;
  const aiYearly = input.aiEnablement ? input.users * aiEnablementPerUserMonthly * 12 : 0;
  const totalYearly = coreYearly + modulesYearly + isoYearly + aiYearly;

  return {
    userPriceMonthly: perUser,
    coreYearly,
    moduleYearly,
    modulesYearly,
    aiYearly,
    isoYearly,
    isoQueries: isoCheck.baseQueries + input.isoCheck.extraPackages * isoCheck.packageQueries,
    totalYearly,
    monthlyExcludingIso: (totalYearly - isoYearly) / 12,
    yearlySaving: input.yearly ? coreGross + modulesGross - coreYearly - modulesYearly : 0
  };
}

export const DEFAULT_CALCULATOR_INPUT: CalculatorInput = {
  yearly: false,
  users: 250,
  sysAdmins: 1,
  orgManagers: 1,
  aiEnablement: true,
  modules: Object.fromEntries(
    CALCULATOR_MODULES.map((module) => [module, { active: false, extraSeats: 0 }])
  ) as CalculatorInput['modules'],
  isoCheck: { active: false, extraPackages: 0 }
};
